/**
 * Chameleon (in-product tours).
 *
 * Nothing here identifies the viewer, and that is deliberate. Chameleon reaches
 * a Forge page as a Segment device-mode destination, and Segment's own
 * integration already maps identify onto it:
 *
 *     Chameleon.prototype.identify = function (identify) {
 *       window.chmln.identify(identify.userId(), extend(traits, opts));
 *     };
 *
 * So `identifyViewer` covers Chameleon for free. The wandb remote's manual
 * `chmln.identify` in `util/analytics.ts` is guarded by `envIsServerDeployment`
 * (`ENVIRONMENT_IS_PRIVATE && ENVIRONMENT_NAME === 'local'`) for exactly that
 * reason: a self-hosted deployment has no Segment to do it, and doing it on
 * SaaS would identify twice.
 *
 * What is left is reporting which experiment bucket Chameleon dropped the
 * viewer into, so tours can be correlated downstream. That belongs to the shell
 * rather than to any one remote: `after:profile` fires once per page, and a
 * visitor who lands on a shell-owned page would otherwise be bucketed with no
 * event and have the listener registered too late to catch it.
 */
import {analyticsForShell, onSegmentReady} from './segment';

declare global {
  interface Window {
    chmln?: {
      on(event: string, callback: () => void): void;
      data?: {profile?: {attributes?: {percent?: unknown}}};
    };
  }
}

/** Module state: the listener is per page, and `on` does not dedupe. */
let registered = false;

/**
 * Reports the viewer's Chameleon experiment bucket, once per page.
 *
 * Waits on `ready` rather than checking for `window.chmln` directly, because
 * the global does not exist until analytics.js has initialized the destination
 * — which is after consent, and after the Segment library itself loads. If
 * Segment never loads, `ready` never fires and neither does this, which is the
 * correct outcome: no Segment means no Chameleon either.
 */
export function trackChameleonBucket(): void {
  if (registered) {
    return;
  }
  registered = true;

  onSegmentReady(() => {
    window.chmln?.on('after:profile', () => {
      analyticsForShell.track('Chameleon Page View', {
        testing_id: window.chmln?.data?.profile?.attributes?.percent,
      });
    });
  });
}

/** Test seam: module state outlives a suite. */
export function resetChameleonForTests(): void {
  registered = false;
}
